/**
 * ReservationShare.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Resultado do envio da reserva ao paciente. Com o número próprio a mensagem já
 * saiu pelo sistema (link em botão); sem ele, mostra a mensagem com o link para
 * "Copiar" ou "Abrir no WhatsApp" já endereçado ao paciente.
 *
 * @module ReservationShare
 */

import React, { useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { copyToClipboard } from '../../utils/copyUtils';
import {
  buildReservationMessage, reservationWhatsAppUrl, RESERVATION_TEMPLATE_KEY, REMINDER_TEMPLATE_KEY,
} from '../../utils/reservationMessage';
import styles from './ReserveSlotDrawer.module.css';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);

const STATUS_TEXT = {
  sent: 'Mensagem enviada pelo WhatsApp da clínica, com o link de cadastro em botão.',
  queued: 'Mensagem na fila do WhatsApp da clínica, com o link de cadastro em botão. Sai em instantes.',
  failed: 'Não foi possível enviar pelo WhatsApp da clínica. Envie a mensagem abaixo pela conversa com o paciente.',
  not_own_number: 'Envie a mensagem abaixo na conversa com o paciente. Quando ele preencher o cadastro, a reserva vira agendamento sozinha.',
};

/**
 * @param {Object} props
 * @param {Object} props.data - resposta de reservations/*.php: { reservation, valid_hours, whatsapp, own_number, message_kind }
 */
export default function ReservationShare({ data }) {
  const { CompanyConfig } = DataProviderInstance();
  const [copied, setCopied] = useState(false);

  const reservation = data?.reservation;
  if (!reservation) return null;

  const sentBySystem = data.whatsapp === 'sent' || data.whatsapp === 'queued';
  const statusText = STATUS_TEXT[data.whatsapp];
  const templateKey = data.message_kind === 'reminder' ? REMINDER_TEMPLATE_KEY : RESERVATION_TEMPLATE_KEY;
  const message = reservation.token
    ? buildReservationMessage(reservation, CompanyConfig, templateKey)
    : '';

  const handleCopy = async () => {
    const result = await copyToClipboard(message);
    if (result?.success) setCopied(true);
  };

  const handleOpenWhatsApp = () => {
    window.open(reservationWhatsAppUrl(reservation, message), '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {statusText && (
        <div
          className={`${styles.statusBox} ${sentBySystem ? styles.statusSuccess : (data.whatsapp === 'failed' ? styles.statusWarning : styles.statusNeutral)}`}
          role="status"
        >
          <span className={styles.statusIcon}>{sentBySystem ? <CheckIcon /> : <InfoIcon />}</span>
          <span>{statusText}</span>
        </div>
      )}

      {message && !sentBySystem && (
        <div className={styles.shareBlock}>
          <textarea
            className={styles.messageBox}
            value={message}
            readOnly
            onFocus={(e) => e.target.select()}
            aria-label="Mensagem para o paciente"
          />
          <div className={styles.shareActions}>
            <button type="button" className={styles.btnSecondary} onClick={handleCopy}>
              {copied ? 'Mensagem copiada' : 'Copiar mensagem'}
            </button>
            <button type="button" className={styles.btnWhatsApp} onClick={handleOpenWhatsApp}>
              Abrir no WhatsApp
            </button>
          </div>
        </div>
      )}
    </>
  );
}
